import type {
  ContentItem,
  IPageDescription,
  ISectionBrief,
  ListContentItem,
  Volume,
} from '@wix/adi-content-api';
import {
  widgetToAppMap,
  AppToAppDefId,
  ContentManager,
} from '@wix/adi-content-api';
import {
  countNumberOfContentItems,
  createFlatStructureMap,
  getLocale,
} from '../utils';
import { caasContentAdapter } from './caasContentAdapter';
import {
  APP_TO_HARD_CODED_SOURCE_TEMPLATE_ID,
  CE_TYPES_THAT_MUST_HAVE_CSM_DEFINITION_TO_RENDER,
  CE_TYPES_TO_ENRICH_MEDIA,
  DEFAULT_INDUSTRY_ID,
  DEFAULT_STRUCTURE_ID,
} from './caasConsts';
import { ContentKind, TEMP_USER_DATA } from '../consts';
import {
  createSectionContent,
  getAppNamesAndAppDefIdsEntries,
  getSourceTemplateIdKey,
  isListContentItem,
} from './caasUtils';
import { CeType } from '@wix/adi-core-types';
import {
  APP_ID_TO_CE_TYPE,
  getCeTypeToAppDefIdsMap,
} from './sitemapRulesConsts';
import {
  addNewSection,
  getCeTypeToSectionIndex,
  getSectionIndex,
  getWidgetPositionCfg,
  isSectionBlocked,
  removeSectionsIfNeeded,
  sortAppDefIdsByPriority,
  replaceContentCategory,
} from './sitemapRulesUtils';

import type {
  AppInstallOption,
  AppInstallOrigin,
} from '@wix/editor-platform-host-integration-apis';
import {
  ExtraContentItem,
  ExtraListContentItem,
  GetContentOptions,
  IndustryProfileData,
  PageData,
  PageSectionContent,
  ProviderContent,
  SourceTemplateIdMapByCeType,
  SuggestedKit,
  UserData,
} from '../types';
import type { CompStructure } from '@wix/document-services-types';
import { BusinessTypeInfo } from '../types';

export class CaasContentProvider {
  private readonly _caas: ContentManager;
  private _profileId: string = '';
  private _industryId: string = '';
  private _structureId: string = '';
  private initPromise: Promise<void> | null = null;

  constructor({
    origin = 'contentProvider',
    locale,
    contentVersion,
  }: {
    origin?: string;
    locale?: string;
    contentVersion?: string;
  }) {
    this._caas = new ContentManager({
      origin,
      locale: getLocale(locale),
      contentVersion,
      flags: {
        getProfilesOnlyFromContentItemsMappedToIndustry: true,
      },
    });
  }

  async initContent(industryId?: string, structureId?: string): Promise<void> {
    const isSameContent =
      this._industryId === industryId && this._structureId === structureId;
    const didInitContent = !!this._industryId && !!this._structureId;
    const hasNoContentId = !industryId && !structureId;
    if (isSameContent || (hasNoContentId && didInitContent)) {
      return;
    }
    const partialIds =
      (!industryId && structureId) || (industryId && !structureId);
    if (partialIds) {
      throw new Error(
        `Missing one of the content ids: industryId=${industryId}, structureId=${structureId}`,
      );
    }
    this._industryId = hasNoContentId ? DEFAULT_INDUSTRY_ID : industryId!;
    this._structureId = hasNoContentId ? DEFAULT_STRUCTURE_ID : structureId!;
    this.initPromise = await this._caas.initContent(
      this._industryId,
      this._structureId,
      {},
    );
    this._profileId = '';
  }

  setUserData(userData: UserData) {
    this._caas.setReplacements({
      'GeneralDescription.BusinessName':
        userData.businessName ?? TEMP_USER_DATA.businessName,
      'GeneralDescription.MainEmail': userData.email ?? TEMP_USER_DATA.email,
      'GeneralDescription.BusinessType':
        userData.businessTypeName ?? TEMP_USER_DATA.businessTypeName,
      'Contact.PhoneNumber': userData.phoneNumber ?? TEMP_USER_DATA.phoneNumber,
      'Contact.email': userData.email ?? TEMP_USER_DATA.email,
    });
  }

  private getCaasContent(
    ceType: CeType,
    numberOfListItems: number | undefined,
    count: number,
  ): ExtraListContentItem[] | ExtraContentItem[] | undefined {
    const MAX_OFFSET = 99;
    const randomOffset = Math.floor(Math.random() * MAX_OFFSET);

    const sections = this._caas
      .getPages()
      ?.map(({ sections }) => sections)
      .flat();
    const fullCeType =
      sections?.filter((section) => section.ceType === ceType)[0] ?? ceType;

    const ceTypeContent = this.generateContent(
      numberOfListItems,
      fullCeType,
      count,
      randomOffset,
    );

    if (!ceTypeContent) {
      return;
    }

    this.enrichMediaContent(
      ceType,
      sections,
      numberOfListItems,
      count,
      randomOffset,
      ceTypeContent,
    );

    return ceTypeContent;
  }

  private generateContent(
    numberOfListItems: number | undefined,
    fullCeType: ISectionBrief | CeType,
    count: number,
    randomOffset: number,
  ) {
    return numberOfListItems
      ? this._caas.generateListContentItems(
          fullCeType,
          count,
          numberOfListItems,
          randomOffset,
        )
      : this._caas.generateContentItems(fullCeType, count, randomOffset);
  }

  private enrichMediaContent(
    ceType: CeType,
    sections: ISectionBrief[] | undefined,
    numberOfListItems: number | undefined,
    count: number,
    randomOffset: number,
    ceTypeContent: ListContentItem[] | ContentItem[],
  ) {
    if (!CE_TYPES_TO_ENRICH_MEDIA.includes(ceType)) {
      return;
    }
    const fullContactCeType =
      sections?.filter((section) => section.ceType === CeType.Contact)[0] ??
      CeType.Contact;
    const extraCeTypeContent = this.generateContent(
      numberOfListItems,
      fullContactCeType,
      count,
      randomOffset,
    );
    if (!Array.isArray(extraCeTypeContent) || !extraCeTypeContent.length) {
      return;
    }
    if (isListContentItem(extraCeTypeContent)) {
      (ceTypeContent as ExtraListContentItem[]).forEach(
        (contentItem: ExtraListContentItem, index: number) => {
          contentItem.items.forEach(
            (item: ExtraContentItem, itemIndex: number) => {
              item.extraMedia =
                extraCeTypeContent[index].items[itemIndex].media;
            },
          );
          contentItem.header.extraMedia =
            extraCeTypeContent[index].header.media;
          contentItem.footer.extraMedia =
            extraCeTypeContent[index].footer.media;
        },
      );
    } else {
      ceTypeContent.forEach((contentItem: ExtraContentItem, index: number) => {
        contentItem.extraMedia = extraCeTypeContent[index].media;
      });
    }
  }

  private getSourceTemplateIdsMap(): SourceTemplateIdMapByCeType {
    const caasSourceTemplateIdsMapByCeType =
      this._caas.getTpaMetasitesIDs() || {};
    const ceTypeToAppDefId = getCeTypeToAppDefIdsMap();
    const caasSourceTemplateIdsMap = Object.entries(
      caasSourceTemplateIdsMapByCeType,
    ).reduce((acc, [ceType, sourceTemplateId]) => {
      return {
        ...acc,
        [ceType]: { appDefId: ceTypeToAppDefId[ceType]?.[0], sourceTemplateId },
      };
    }, {} as SourceTemplateIdMapByCeType);
    const hardCodedSourceTemplateIdsMap = Object.entries(
      APP_TO_HARD_CODED_SOURCE_TEMPLATE_ID,
    ).reduce((acc, [appDefId, sourceTemplateId]) => {
      const ceTypes = APP_ID_TO_CE_TYPE[appDefId];
      if (ceTypes) {
        ceTypes.forEach((ceType) => {
          acc[ceType] = { sourceTemplateId, appDefId };
        });
      }
      return acc;
    }, {} as SourceTemplateIdMapByCeType);
    return { ...caasSourceTemplateIdsMap, ...hardCodedSourceTemplateIdsMap };
  }

  private gatherCaasAvailableAppDefIds(): string[] {
    const ceTypeToAppDefId = getCeTypeToAppDefIdsMap();
    const appsFromCaas = Object.keys(this.getSourceTemplateIdsMap())
      .map((ceTypeOrWidgetType) => {
        const appTypeOrCeType =
          widgetToAppMap[ceTypeOrWidgetType] || ceTypeOrWidgetType;
        return (
          AppToAppDefId[appTypeOrCeType] ||
          ceTypeToAppDefId[appTypeOrCeType as CeType]
        );
      })
      .flat()
      .filter(Boolean) as string[];
    return [...new Set(appsFromCaas)];
  }

  private filterSectionsByAppDefIds(
    sections: PageSectionContent[],
    chosenAppDefIds: string[],
  ): PageSectionContent[] {
    const allAvailableAppDefIds = new Set([
      ...this.gatherCaasAvailableAppDefIds(),
      ...chosenAppDefIds,
    ]);
    const ceTypeToAppDefId = getCeTypeToAppDefIdsMap();
    return sections.filter((section) => {
      if (
        CE_TYPES_THAT_MUST_HAVE_CSM_DEFINITION_TO_RENDER.has(section.ceType)
      ) {
        const appDefIds = ceTypeToAppDefId[section.ceType];
        if (appDefIds?.length) {
          return appDefIds.some((appDefId) =>
            allAvailableAppDefIds.has(appDefId),
          );
        }
      }
      return true;
    });
  }

  async getContent(
    { industryId, structureId, contentType }: GetContentOptions,
    compStructure: CompStructure,
    contentKind: ContentKind = ContentKind.ALL,
  ): Promise<ProviderContent[]> {
    await this.initContent(industryId, structureId);
    const flatStructureMap = createFlatStructureMap(compStructure);
    const numberOfRegularItems =
      countNumberOfContentItems(flatStructureMap) || 1;
    const numberOfListItems = countNumberOfContentItems(flatStructureMap, true);
    const caasContentItems = contentType
      ? this.getCaasContent(
          contentType,
          numberOfListItems,
          numberOfRegularItems,
        )
      : undefined;
    return caasContentAdapter(caasContentItems, contentKind);
  }

  public getPageSectionsContents(
    sections: ISectionBrief[],
    installedAppDefIds: string[],
    appDefIdsNotToPersonalize: string[],
    useSitemapRules = true,
  ): PageSectionContent[] {
    const sourceTemplateIdsByCeType = this.getSourceTemplateIdsMap();
    const ceTypesNotToPersonalize = new Set<CeType>(
      appDefIdsNotToPersonalize
        .map((appDefId) => APP_ID_TO_CE_TYPE[appDefId])
        .flat(),
    );
    const sectionContents = sections
      .map(({ ceType, volume, id }) =>
        createSectionContent(
          id as string,
          ceType as CeType,
          volume as Volume,
          ceTypesNotToPersonalize,
          sourceTemplateIdsByCeType,
        ),
      )
      .filter(({ contentCategory }) => contentCategory);
    return useSitemapRules
      ? this.applySitemapRules(
          sectionContents,
          installedAppDefIds,
          ceTypesNotToPersonalize,
          sourceTemplateIdsByCeType,
        )
      : this.filterSectionsByAppDefIds(sectionContents, installedAppDefIds);
  }

  private applySitemapRules(
    sections: PageSectionContent[],
    installedAppDefIds: string[],
    ceTypesNotToPersonalize: Set<CeType>,
    sourceTemplateIdsByCeType: SourceTemplateIdMapByCeType,
  ): PageSectionContent[] {
    let resultSections = [...sections];
    const sortedAppDefIds = sortAppDefIdsByPriority(installedAppDefIds);
    for (const appDefId of sortedAppDefIds) {
      let ceTypeToSectionIndex = getCeTypeToSectionIndex(resultSections);
      const positionCfg = getWidgetPositionCfg(
        appDefId,
        this._structureId,
        sections,
      );
      if (!positionCfg) {
        continue;
      }
      if (positionCfg.appCeType) {
        const sectionIndex = getSectionIndex(
          positionCfg,
          ceTypeToSectionIndex,
          resultSections.length,
        );
        const isBlocked = isSectionBlocked(
          this._structureId,
          positionCfg,
          sortedAppDefIds,
        );
        if (!isBlocked) {
          resultSections = addNewSection(
            appDefId,
            positionCfg.appCeType,
            resultSections,
            sectionIndex,
            ceTypesNotToPersonalize,
            sourceTemplateIdsByCeType,
          );
          ceTypeToSectionIndex = getCeTypeToSectionIndex(resultSections);
        }
        if (positionCfg.replaceContentCategory) {
          resultSections = replaceContentCategory(
            sections,
            positionCfg.appCeType,
            positionCfg.replaceContentCategory,
          );
        }
      }
      resultSections = removeSectionsIfNeeded(
        resultSections,
        positionCfg,
        ceTypeToSectionIndex,
      );
    }
    resultSections = this.filterSectionsByAppDefIds(
      resultSections,
      installedAppDefIds,
    );
    return resultSections;
  }

  async initAdditionalPagesData() {
    await this.initPromise;
    const pages = this._caas.getPages();

    if (!pages?.length) {
      return [];
    }

    return pages.slice(1);
  }

  async getAdditionalPagesData(
    additionalPages: IPageDescription[],
  ): Promise<PageData[]> {
    await this.initPromise;

    return additionalPages.map(({ name, sections }) => ({
      sections: this.getPageSectionsContents(sections, [], []),
      name,
    }));
  }

  async getInitialHomepageData() {
    await this.initPromise;
    const pages = this._caas.getPages();
    if (!pages?.length) {
      throw new Error('No pages found in CAAS provider');
    }
    const [homepage] = pages;
    return homepage;
  }

  async getHomepageData(
    appDefIds: string[],
    appDefIdsNotToPersonalize: string[],
    initialHomepageData: IPageDescription,
  ): Promise<PageData> {
    await this.initPromise;
    const sections = this.getPageSectionsContents(
      initialHomepageData.sections,
      appDefIds,
      appDefIdsNotToPersonalize,
    );
    return {
      name: initialHomepageData.name,
      sections,
    };
  }

  async getIndustryProfiles(): Promise<IndustryProfileData[]> {
    await this.initPromise;
    const profileMapping = this._caas.getAllProfileMappings();
    if (!profileMapping?.length) {
      return [];
    }
    return profileMapping.map(({ profileId, description, translationKey }) => ({
      id: profileId,
      description: description || `${profileId} description`,
      translationKey: translationKey || '',
    }));
  }

  private verifyProfileIdExist(id: string) {
    const profileIdExist = this._caas
      .getAllProfileMappings()
      ?.some(({ profileId }) => id === profileId);
    if (!profileIdExist) {
      throw new Error(`Profile id: ${id} does not exist`);
    }
  }

  setProfileId(profileId: string): void {
    this.verifyProfileIdExist(profileId);
    this._profileId = profileId;
    this._caas.setProfileId(profileId);
  }

  async getCurrentProfile(): Promise<IndustryProfileData | null> {
    if (!this._profileId) {
      return null;
    }
    const currentIndustryProfiles = (await this.getIndustryProfiles()).find(
      ({ id }) => id === this._profileId,
    );
    return currentIndustryProfiles || null;
  }

  async getLayoutFamilyBlacklist(): Promise<string[]> {
    await this.initPromise;
    const blacklistLayoutFamilies = this._caas.getLayoutFamilyBlacklist();
    if (!blacklistLayoutFamilies?.length) {
      return [];
    }
    return blacklistLayoutFamilies;
  }

  async getSuggestedKits(): Promise<SuggestedKit[]> {
    await this.initPromise;
    if (!this._profileId) {
      return [];
    }
    return (
      this._caas
        .getProfileMapping()
        ?.kits?.map(({ id, level }) => ({ id, volume: level as Volume })) || []
    );
  }

  getAppsInstallOptions(
    appDefIds: string[],
  ): Record<string, Partial<AppInstallOption>> {
    const templateIdsMap = this.getSourceTemplateIdsMap();
    if (!templateIdsMap) return {};
    const appNamesAndIdsEntries = getAppNamesAndAppDefIdsEntries(appDefIds);
    return appNamesAndIdsEntries.reduce(
      (appsOptions, [appName, appDefId]) => {
        const templateKey = getSourceTemplateIdKey(appName, templateIdsMap);
        if (!templateKey) return appsOptions;

        const { sourceTemplateId } = templateIdsMap[templateKey];
        if (sourceTemplateId) {
          appsOptions[appDefId] = {
            sourceTemplateId,
            origin: { type: 'CLASSIC' } as AppInstallOrigin,
          };
        }
        return appsOptions;
      },
      {} as Record<string, Partial<AppInstallOption>>,
    );
  }

  async getBusinessTypeByTerm(term: string): Promise<BusinessTypeInfo | null> {
    await this.initPromise;
    const suggestions = await this._caas.suggest(term);
    const suggestion = suggestions?.results?.[0];
    if (!suggestion) {
      return null;
    }
    return {
      displayName: suggestion.displayName,
      industryId: suggestion.industryId,
      structureId: suggestion.structureId,
    };
  }

  async getIsCaasInitialized() {
    return this.initPromise;
  }
}
